import countries from "i18n-iso-countries";
import { toCountryName } from "./countryName";

// CDB 2020 forms returned by the Swiss ownership API (docs/cdb-forms-rules.md)
// and their printable versions in public/forms/cdb/.

export const CDB_NOT_APPLICABLE = "N/A (not applicable)";

const COUNTRY_NAME_OVERRIDES = { US: "United States", USA: "United States" };

/** Country codes (ISO 2 or 3 letters) are written as full names on the forms; other text is kept. */
export function cdbCountryText(value) {
  const text = String(value || "").trim();
  if (!/^[A-Za-z]{2,3}$/.test(text)) return text;
  return COUNTRY_NAME_OVERRIDES[text.toUpperCase()] || toCountryName(text);
}

/** True when an address holds only a country (code or name), so the full address is still to be entered. */
export function cdbIsCountryOnly(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  if (/^[A-Za-z]{2,3}$/.test(text)) return !!COUNTRY_NAME_OVERRIDES[text.toUpperCase()] || toCountryName(text) !== text;
  return !!countries.getAlpha2Code(text, "en");
}

export const CDB_FORM_META = {
  A: { title: "Declaration of identity of the beneficial owner", file: "form-a.html" },
  K: { title: "Establishing of the controlling person", file: "form-k.html" },
  S: { title: "Foundations (as well as similar constructs)", file: "form-s.html" },
  T: { title: "Declaration for trusts", file: "form-t.html" },
};

export function cdbFormUrl(form) {
  const meta = CDB_FORM_META[form?.form];
  return meta ? `${process.env.PUBLIC_URL || ""}/forms/cdb/${meta.file}` : "";
}

/** Name as listed on a form; entities with their own form point to it. */
export function cdbEntryName(entry, forms = []) {
  const letter = entry?.seeForm ? forms.find((f) => f.id === entry.seeForm)?.form : "";
  return letter ? `${entry.name} (see Form ${letter})` : entry?.name || "";
}

/** Form data in the shape expected by public/forms/cdb/cdb-forms.js (CDBForm.fill). */
export function cdbFormFillPayload(form, forms = []) {
  // Entities have no nationality, and their incorporation date goes in the date-of-birth field.
  const toRow = (entry) => ({
    name: cdbEntryName(entry, forms),
    dateOfBirth: (entry.kind === "entity" ? entry.incorporationDate : entry.dateOfBirth) || "",
    nationality: entry.kind === "entity" ? CDB_NOT_APPLICABLE : cdbCountryText(entry.nationality),
    domicile: cdbCountryText(entry.domicile),
    domicileCountryOnly: cdbIsCountryOnly(entry.domicile),
    // Shown on the form as "Date of birth required" / "Date of incorporation required".
    dateOfBirthMissing: (entry.kind === "entity" ? entry.incorporationDate : entry.dateOfBirth)
      ? false
      : entry.kind === "entity" ? "entity" : "person",
  });
  const boxes = Array.isArray(form.controlBoxes) ? form.controlBoxes : [];
  const groups = Object.fromEntries(Object.entries(form.sections || {}).map(([key, list]) => [key, (list || []).map(toRow)]));
  if (form.form === "T") {
    // myKyc records neither re-settlements nor further persons: point 3 and point 5 b) are always "N/A",
    // point 5 a) when no protector is recorded. The user can still edit these fields in the form.
    // In an "N/A" block (point 3, 5 a, 5 b), the other fields also read "N/A".
    const notApplicablePerson = { name: CDB_NOT_APPLICABLE, domicile: "N/A", dateOfBirth: "N/A", nationality: "N/A" };
    groups.previousSettlors = [{ ...notApplicablePerson, dateOfDeath: "N/A" }];
    if (!groups.protectors?.length) groups.protectors = [notApplicablePerson];
    groups.furtherPersons = [notApplicablePerson];
  }
  return {
    fields: {
      contractingPartner: form.contractingPartner || "",
      ...(form.form === "S" || form.form === "T" ? {
        arrangementName: form.subjectName || "",
        beneficiaryGroups1: form.beneficiaryClass || "",
      } : {}),
      ...(form.form === "K" ? {
        controlBox1: boxes.includes(1),
        controlBox2: boxes.includes(2),
        controlBox3: boxes.includes(3),
        fiduciary: "no",
      } : {}),
    },
    groups,
  };
}
